/**
 * OTP Verification - Mad green/black theme (same as Login/Signup).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast, useI18n } from '../../providers';
import { useUIStore, useAuthStore } from '../../store';
import { ROUTES } from '../../utils/constants';

const OTPVerification = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifySignupOTP, verifyLoginOTP, resendOTP, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language, formatPhoneNumber } = useI18n();

  const phone = location.state?.phone || '';
  const transactionId = location.state?.transactionId;
  const isLogin = location.state?.isLogin || false;

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (otp.every((digit) => digit !== '') && otp.join('').length === 6) {
      handleVerify();
    }
  }, [otp]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    setResendTimer(60);
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setOtp(newOtp.slice(0, 6));
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      showError(
        language === 'fr'
          ? 'Veuillez entrer le code à 6 chiffres complet'
          : 'Please enter the complete 6-digit code'
      );
      return;
    }
    try {
      if (isLogin) {
        await verifyLoginOTP({ phone, code });
      } else {
        await verifySignupOTP({ phone, code });
      }
      showSuccess(language === 'fr' ? 'Vérification réussie!' : 'Verification successful!');
      useUIStore.getState().clearToasts();
      if (onSuccess) onSuccess();
      else if (isLogin) {
        const user = useAuthStore.getState().user;
        const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
        if (isAdmin) navigate(ROUTES.ADMIN_DASHBOARD, { replace: true });
        else if (transactionId) navigate(`/transaction/${transactionId}/pay`);
        else navigate(ROUTES.DASHBOARD, { replace: true });
      } else if (transactionId) navigate(`/transaction/${transactionId}/pay`);
      else navigate(ROUTES.DASHBOARD, { replace: true });
    } catch (error) {
      // Use message string only; never show user input (e.g. OTP) from errors[].value or errors[0]
      let errorMessage =
        typeof error?.message === 'string'
          ? error.message
          : language === 'fr'
          ? 'Code de vérification invalide'
          : 'Invalid verification code';
      const firstError = error?.errors?.[0];
      if (firstError && typeof firstError?.message === 'string') {
        errorMessage = firstError.message;
      } else if (typeof firstError === 'string' && !/^\d{6}$/.test(firstError)) {
        // Use firstError only if it's not a 6-digit OTP (never show OTP in UI)
        errorMessage = firstError;
      }
      showError(errorMessage);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    try {
      await resendOTP({
        phone,
        purpose: isLogin ? 'login' : 'phone_verification',
      });
      showSuccess(
        language === 'fr' ? 'Code de vérification renvoyé!' : 'Verification code resent!'
      );
      setResendTimer(60);
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.errors?.[0] ||
        (language === 'fr' ? 'Échec du renvoi du code' : 'Failed to resend code');
      showError(errorMessage);
    }
  };

  const formattedPhone = formatPhoneNumber ? formatPhoneNumber(phone) : phone;

  return (
    <div className="auth-mad">
      <div className="auth-mad-bg" aria-hidden="true" />
      <div className="auth-mad-grid" aria-hidden="true" />
      <div className="auth-mad-inner">
        <button type="button" onClick={() => navigate(-1)} className="auth-mad-back">
          <svg
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 19l-7-7 7-7" />
          </svg>
          <span>{language === 'fr' ? 'Retour' : 'Back'}</span>
        </button>

        <div className="auth-mad-card">
          <div className="auth-mad-header">
            <div className="auth-mad-icon" aria-hidden>
              🔐
            </div>
            <h1 className="auth-mad-title">
              {language === 'fr' ? 'Entrez le code à 6 chiffres' : 'Enter 6-digit code'}
            </h1>
            <p className="auth-mad-sub">
              {language === 'fr' ? 'Envoyé à' : 'Sent to'} {formattedPhone || phone}
            </p>
          </div>

          <div className="auth-mad-form">
            <div className="auth-mad-otp-row">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="auth-mad-otp-digit"
                  autoComplete="one-time-code"
                  autoFocus={index === 0}
                  aria-label={language === 'fr' ? `Chiffre ${index + 1}` : `Digit ${index + 1}`}
                />
              ))}
            </div>

            <div className="auth-mad-resend">
              {resendTimer > 0 ? (
                <p className="auth-mad-resend-timer">
                  {language === 'fr' ? 'Renvoyer le code dans' : 'Resend code in'}{' '}
                  {Math.floor(resendTimer / 60)}:{(resendTimer % 60).toString().padStart(2, '0')}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  className="auth-mad-resend-btn"
                  disabled={loading}
                >
                  {language === 'fr' ? 'Renvoyer le code' : 'Resend code'}
                </button>
              )}
            </div>

            <button
              type="button"
              className="auth-mad-btn"
              onClick={handleVerify}
              disabled={loading || otp.join('').length !== 6}
            >
              {loading
                ? language === 'fr'
                  ? 'Vérification…'
                  : 'Verifying…'
                : language === 'fr'
                ? 'Vérifier'
                : 'Verify'}
            </button>

            <p className="auth-mad-otp-hint">
              {language === 'fr'
                ? '✓ Vérification automatique lorsque les 6 chiffres sont saisis'
                : '✓ Auto-verifies when all 6 digits are entered'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
